import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import i18n from "i18next";
export type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { type RootState } from "app/store";
import { selectConfig } from "slices/explorer";

/**
 * The headers every data-v2 request carries. Exported so requests issued outside RTK Query
 * reach the server looking the same as the ones the API slice sends.
 */
export function dataV2Headers(state: RootState, headers = new Headers()): Headers {
    const {
        auth: { accessToken },
    } = state;

    if (accessToken) {
        headers.set("authorization", `Bearer ${accessToken}`);

        if (!headers.has("Accept-Language")) {
            headers.set("Accept-Language", i18n.language);
        }
    }

    return headers;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: (headers, { getState }) => dataV2Headers(getState() as RootState, headers),
});

export function getDataV2DynamicBaseQuery(suffix = "") {
    const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
        args,
        api,
        extraOptions,
    ) => {
        const baseUrl = selectConfig(api.getState() as RootState).dataV2ServerUrl + suffix;
        if (!baseUrl) {
            return {
                error: {
                    status: 400,
                    statusText: "Bad Request",
                    data: "data-v2 URL is not defined",
                },
            };
        }
        const urlEnd = typeof args === "string" ? args : args.url;
        const adjustedUrl = `${baseUrl}${urlEnd}`;
        const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };

        return rawBaseQuery(adjustedArgs, api, extraOptions);
    };

    return dynamicBaseQuery;
}

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === "object" && error !== null && "status" in error;
}

/**
 * Unwraps a lazy query, aborting the request if `signal` aborts first. An abort resolves to
 * `undefined` rather than rejecting, so callers only have to handle real failures.
 */
export async function unwrapAbortable<T>(
    request: { unwrap(): Promise<T>; abort(): void },
    signal: AbortSignal,
): Promise<T | undefined> {
    if (signal.aborted) {
        request.abort();
        return undefined;
    }

    const abort = () => request.abort();
    signal.addEventListener("abort", abort, { once: true });

    try {
        return await request.unwrap();
    } catch (e) {
        if (signal.aborted) {
            return undefined;
        }
        throw e;
    } finally {
        signal.removeEventListener("abort", abort);
    }
}
